import React from 'react';
import { bool, func, oneOf, string } from 'prop-types';
import { useIntl } from 'react-intl';

import { PURPOSES, contractFor, isConsentAvailable, CANONICAL_LOCALE } from '../../util/aiConsent';
import css from './AiConsentGate.module.css';

/**
 * The notice a person reads before a feature is allowed to send their data out.
 *
 * One component for every surface that needs one, so the shape of the ask is
 * the same everywhere and a new surface cannot invent a friendlier version of
 * it. The wording is not a prop — it comes from the contract registry, keyed by
 * purpose, which is the same registry the server checks against. A surface can
 * choose *when* to ask; it cannot choose what the sentence says.
 *
 * Four properties this component exists to guarantee:
 *
 *   - nothing is pre-agreed. There is no `defaultConsented` prop and no
 *     `checked` prop, because the only way to consent is to press the button;
 *   - the notice renders *before* the thing it guards, never beside or after
 *     it. Callers render this instead of the feature, not next to it;
 *   - declining is a real option with a real control, not just "don't click".
 *     `onDecline` renders a second button so refusal is as reachable as
 *     agreement;
 *   - in a locale with no approved translation it renders nothing at all, so a
 *     caller that forgot to check `isConsentAvailable` fails closed rather than
 *     showing an English privacy contract to somebody who did not choose
 *     English. The surrounding feature must be hidden too — this is the last
 *     line, not the only one.
 *
 * The contract text itself is deliberately *not* an intl message. It is
 * canonical English until a reviewed translation exists, and a translation file
 * is exactly where an unreviewed one would appear. Only the surrounding
 * controls are localised.
 *
 * ## The heading, and what it may not do
 *
 * The notice used to render as a grey strip of small text ending in "do not
 * type personal or sensitive information", directly under the chat's suggested
 * questions. Every fact in it was correct and it read as a warning — the last
 * thing somebody sees before deciding whether this product is safe to talk to.
 *
 * The heading reframes the same sentence as an explanation of how the feature
 * works, which is what it is, and a surface supplies its own through `heading`
 * so it can match its own voice. The risk that buys is obvious: a reassuring
 * heading can make the notice under it feel like a formality. It is bounded
 * rather than prevented — the heading is presentational only, `contract.text`
 * below it is not a prop and cannot be edited by a caller, and the decline
 * control is not a caller's decision either. A surface may set the tone of the
 * introduction; it cannot change what is being disclosed or remove the refusal.
 */
const AiConsentGate = props => {
  const {
    purpose,
    onAccept,
    onDecline,
    locale = CANONICAL_LOCALE,
    acceptLabel,
    declineLabel,
    compact = false,
    className,
    heading,
    icon,
  } = props;

  const intl = useIntl();
  const contract = contractFor(purpose);

  // Fail closed: unknown purpose, or a locale nobody has approved wording for.
  if (!contract || !isConsentAvailable(purpose, locale)) return null;

  const rootClass = [
    css.root,
    compact ? css.compact : null,
    heading ? css.welcome : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const accept =
    acceptLabel || intl.formatMessage({ id: 'AiConsentGate.accept', defaultMessage: 'I agree' });
  const decline =
    declineLabel ||
    intl.formatMessage({ id: 'AiConsentGate.decline', defaultMessage: 'No thanks' });

  return (
    <div
      className={rootClass}
      data-testid="ai-consent-gate"
      data-purpose={purpose}
      role="region"
      aria-label={heading || accept}
      aria-live="polite"
    >
      {/* Purely presentational framing around the mandatory notice below — a
          surface may add a warmer introduction, but the sentence it introduces
          is still the one from the contract registry, unedited. */}
      {heading && (
        <div className={css.header}>
          {icon && (
            <span className={css.avatar} aria-hidden="true">
              {icon}
            </span>
          )}
          <p className={css.heading}>{heading}</p>
        </div>
      )}
      {/* lang is set explicitly: the contract is English even when the rest of
          the page is not, and a screen reader should say so. */}
      <p className={css.text} lang={CANONICAL_LOCALE}>
        {contract.text}
      </p>
      <div className={css.actions}>
        <button
          type="button"
          className={css.accept}
          onClick={onAccept}
          data-testid="ai-consent-accept"
        >
          {accept}
        </button>
        {onDecline ? (
          <button
            type="button"
            className={css.decline}
            onClick={onDecline}
            data-testid="ai-consent-decline"
          >
            {decline}
          </button>
        ) : null}
      </div>
      {/* Rendered, not just implied. Somebody deciding whether to agree should
          be able to see which contract they are agreeing to. */}
      <p className={css.version}>
        {intl.formatMessage(
          { id: 'AiConsentGate.policyVersion', defaultMessage: 'Policy version {version}' },
          { version: contract.version }
        )}
      </p>
    </div>
  );
};

AiConsentGate.propTypes = {
  purpose: oneOf(Object.values(PURPOSES)).isRequired,
  onAccept: func.isRequired,
  onDecline: func,
  /** UI locale. Anything without an approved translation renders nothing. */
  locale: string,
  acceptLabel: string,
  declineLabel: string,
  compact: bool,
  className: string,
  /** Optional warm introduction shown above the (unedited) contract text. */
  heading: string,
  /** Optional icon/emoji shown beside the heading. Ignored without `heading`. */
  icon: string,
};

export default AiConsentGate;
